import {
  Badge,
  Box,
  CircularProgress,
  CircularProgressLabel,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Progress,
  Select,
  SimpleGrid,
  Skeleton,
  SkeletonText,
  Stat,
  StatArrow,
  StatHelpText,
  StatLabel,
  StatNumber,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import {
  AdminLayout,
  Button,
  Card,
  DataTable,
  SearchInput,
  SelectInput,
  StatsCard,
} from '@grcs/components';
import { TableColumn } from '@grcs/components/types';
import {
  useHomeworkOperations,
  useSemesterOperations,
} from '@grcs/hook/useDatabase';
import { useLocalStorage } from '@grcs/hook/useLocalStorage';
import {
  ArticleProfile,
  HomeworkStudentProfile,
  HomeworkV2Profile,
} from '@grcs/types/DataTypes';
import { formatDate } from '@grcs/utils/formatting/formatDate';
import { isNotUndefined } from '@grcs/utils/isNotUndefined';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FiAward,
  FiBarChart,
  FiBookOpen,
  FiCalendar,
  FiClock,
  FiEye,
  FiFilter,
  FiGrid,
  FiInfo,
  FiList,
  FiTarget,
  FiTrendingUp,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

interface CompletedHomeworkWithDetails extends HomeworkV2Profile {
  article?: ArticleProfile;
  completedAt?: string;
  timeSpent?: number;
  score?: number;
  status?: 'excellent' | 'good' | 'needs_improvement';
  attempts?: number;
}

interface CompletionStats {
  totalCompleted: number;
  averageScore: number;
  totalTimeSpent: number;
  completionRate: number;
}

interface Achievement {
  id: string;
  title: string;
  description: string;
  icon: string;
  earned: boolean;
  progress?: number;
  maxProgress?: number;
}

type ViewMode = 'grid' | 'list' | 'table';
type SortBy = 'completed_date' | 'score' | 'title' | 'time_spent';
type FilterBy = 'all' | 'excellent' | 'good' | 'needs_improvement';
type MenuLanguage = 'en' | 'zh';

const menuText = {
  en: {
    menuLanguageLabel: 'Menu Language',
    menuLanguageEnglish: 'English',
    menuLanguageChinese: '中文',
    headerTitle: 'Completed Homework',
    completedSubtitle: (count: number) => `${count} assignments completed`,
    semesterLabel: (semester: string) => `Semester ${semester}`,
    gridView: 'Grid View',
    listView: 'List View',
    tableView: 'Table View',
    statsCompleted: 'Completed',
    statsAverageScore: 'Average Score',
    statsTimeSpent: 'Time Spent',
    timeSpentValue: (hours: number) => `${hours}h`,
    timeSpentSubtitle: (minutes: number) => `${minutes}m total`,
    tabsAllCompleted: (count: number) => `All Completed (${count})`,
    tabsRecentPerformance: 'Recent Performance',
    emptyTitle: 'No completed homework yet',
    tableTitle: 'Title',
    tableScore: 'Score',
    tableTimeSpent: 'Time Spent',
    tableCompleted: 'Completed',
    tableActions: 'Actions',
    viewDetails: 'View Details',
    review: 'Review',
    timeLabel: 'Time',
    attemptsLabel: 'Attempts',
    completedLabel: 'Completed',
    recentScoresTrend: 'Recent Scores Trend',
    performanceSummary: 'Performance Summary',
    bestScore: 'Best Score',
    personalBest: 'Personal best',
    homeworkDetails: 'Homework Details',
    finalScore: 'Final Score',
    timeSpentLabel: 'Time Spent',
    attemptsLabelDetail: 'Attempts',
    levelLabel: 'Level',
    performanceAnalysis: 'Performance Analysis',
    statusLabel: 'Status',
    completedAtLabel: 'Completed',
    timeEfficiencyLabel: 'Time Efficiency',
    timeEfficiencyFast: 'Fast',
    timeEfficiencyAverage: 'Average',
    timeEfficiencyThorough: 'Thorough',
    close: 'Close',
    reviewHomework: 'Review Homework',
    gradeLabel: (grade: string) => `Grade ${grade}`,
    minutesShort: (minutes?: number) => `${minutes}m`,
    minutesLong: (minutes?: number) => `${minutes} min`,
  },
  zh: {
    menuLanguageLabel: '菜单语言',
    menuLanguageEnglish: 'English',
    menuLanguageChinese: '中文',
    headerTitle: '已完成作业',
    completedSubtitle: (count: number) => `已完成 ${count} 项作业`,
    semesterLabel: (semester: string) => `学期 ${semester}`,
    gridView: '网格',
    listView: '列表',
    tableView: '表格',
    statsCompleted: '已完成',
    statsAverageScore: '平均得分',
    statsTimeSpent: '用时',
    timeSpentValue: (hours: number) => `${hours}小时`,
    timeSpentSubtitle: (minutes: number) => `共${minutes}分钟`,
    tabsAllCompleted: (count: number) => `全部已完成 (${count})`,
    tabsRecentPerformance: '近期表现',
    emptyTitle: '暂无已完成作业',
    tableTitle: '标题',
    tableScore: '得分',
    tableTimeSpent: '用时',
    tableCompleted: '完成时间',
    tableActions: '操作',
    viewDetails: '查看详情',
    review: '复习',
    timeLabel: '用时',
    attemptsLabel: '尝试次数',
    completedLabel: '完成',
    recentScoresTrend: '近期得分趋势',
    performanceSummary: '表现概览',
    bestScore: '最高得分',
    personalBest: '个人最佳',
    homeworkDetails: '作业详情',
    finalScore: '最终得分',
    timeSpentLabel: '用时',
    attemptsLabelDetail: '尝试次数',
    levelLabel: '级别',
    performanceAnalysis: '表现分析',
    statusLabel: '状态',
    completedAtLabel: '完成时间',
    timeEfficiencyLabel: '用时效率',
    timeEfficiencyFast: '快',
    timeEfficiencyAverage: '一般',
    timeEfficiencyThorough: '细致',
    close: '关闭',
    reviewHomework: '复习作业',
    gradeLabel: (grade: string) => `${grade} 年级`,
    minutesShort: (minutes?: number) => `${minutes}分钟`,
    minutesLong: (minutes?: number) => `${minutes} 分钟`,
  },
} as const;

export const StudentCompletedEnhanced: React.FC = () => {
  const [user] = useLocalStorage('user', {});
  const homeworkOps = useHomeworkOperations();
  const semesterOps = useSemesterOperations();
  const navigate = useNavigate();
  const toast = useToast();
  const {
    isOpen: isDetailModalOpen,
    onOpen: onDetailModalOpen,
    onClose: onDetailModalClose,
  } = useDisclosure();

  // Data state
  const [, setHomeworkStudent] = useState<HomeworkStudentProfile[]>([]);
  const [completedHomeworks, setCompletedHomeworks] = useState<
    CompletedHomeworkWithDetails[]
  >([]);
  const [selectedHomework, setSelectedHomework] =
    useState<CompletedHomeworkWithDetails | null>(null);
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [currentSemester, setCurrentSemester] = useState<string>('');
  const [error, setError] = useState<string>('');

  // Loading states
  const [loading, setLoading] = useState(true);

  // UI state
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [sortBy, setSortBy] = useState<SortBy>('completed_date');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterBy, setFilterBy] = useState<FilterBy>('all');
  const [menuLanguage, setMenuLanguage] = useLocalStorage(
    'user_language',
    'zh',
  );
  const resolvedMenuLanguage: MenuLanguage =
    menuLanguage === 'zh' ? 'zh' : 'en';
  const t = menuText[resolvedMenuLanguage];

  // Stats
  const [completionStats, setCompletionStats] = useState<CompletionStats>({
    totalCompleted: 0,
    averageScore: 0,
    totalTimeSpent: 0,
    completionRate: 0,
  });

  // Load current semester with comprehensive error handling
  useEffect(() => {
    const loadCurrentSemester = async () => {
      try {
        console.log(
          '🗓️ Loading current semester for Student Completed page...',
        );
        const { data, error: semesterError } =
          await semesterOps.getCurrentSemesterYear();

        if (semesterError) {
          console.error('❌ Error fetching current semester:', semesterError);
          setError(
            'Failed to load semester information. Please try refreshing the page or contact your administrator.',
          );
          return;
        }

        if (data && data.length > 0) {
          const year = data[0].semester_year;
          console.log(`✅ Active semester found: ${year}`);
          setCurrentSemester(year);
          setError(''); // Clear any previous errors
        } else {
          console.warn('⚠️ No active semester found');
          setError(
            'No active semester is currently set. Please contact your teacher to create and activate a semester. You cannot access homework until a semester is active.',
          );
          // Don't load homework data if no active semester
          return;
        }
      } catch (err) {
        console.error('💥 Unexpected error loading current semester:', err);
        setError(
          'An unexpected error occurred while loading semester information. Please try refreshing the page or contact your administrator.',
        );
      }
    };

    if (!currentSemester) {
      loadCurrentSemester();
    }
  }, [currentSemester, semesterOps]);

  // Initialize achievements
  useEffect(() => {
    const mockAchievements: Achievement[] = [
      {
        id: 'first_completion',
        title: 'First Steps',
        description: 'Complete your first homework',
        icon: '🎯',
        earned: completedHomeworks.length > 0,
      },
      {
        id: 'perfect_score',
        title: 'Perfect Score',
        description: 'Achieve 100% on any homework',
        icon: '⭐',
        earned: completedHomeworks.some((h) => (h.score || 0) >= 100),
      },
      {
        id: 'speed_reader',
        title: 'Speed Reader',
        description: 'Complete 10 homework assignments',
        icon: '📚',
        earned: completedHomeworks.length >= 10,
        progress: completedHomeworks.length,
        maxProgress: 10,
      },
    ];
    setAchievements(mockAchievements);
  }, [completedHomeworks]);

  // Fetch completed homework data
  const fetchCompletedHomework = useCallback(async () => {
    if (!currentSemester || error) {
      console.log(
        '⏭️ Skipping homework data loading - no active semester or error present',
      );
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const student_token = user.user_token;

      // Clear any cached data to ensure fresh results after homework completion
      console.log('🔄 Clearing completed homework cache...');
      const cacheKeys = Object.keys(sessionStorage).filter(
        (key) =>
          key.includes('article-analytics') || key.includes('leaderboard'),
      );
      cacheKeys.forEach((key) => {
        sessionStorage.removeItem(key);
        console.log(`- Cleared cache: ${key}`);
      });

      // Get student's homework assignments for current semester
      console.log('📊 Fetching completed homework...');
      const { data: studentHomework, error: homeworkError } =
        await homeworkOps.getHomeworksByStudentId(student_token);
      if (homeworkError) throw new Error('Error fetching student homework');

      if (studentHomework && studentHomework.length > 0) {
        setHomeworkStudent(studentHomework);

        // Filter completed homeworks based on question_attempted field (consistent with analytics logic)
        const completed = studentHomework.filter(isNotUndefined).filter((h) => {
          // Check if homework has been attempted by looking at question_attempted field
          if (!h.question_attempted) return false;

          try {
            const attempted = JSON.parse(h.question_attempted);
            return Array.isArray(attempted)
              ? attempted.length > 0
              : Object.keys(attempted).length > 0;
          } catch (e) {
            console.warn(
              `Failed to parse question_attempted for ${h.homework_token}:`,
              e,
            );
            return false;
          }
        });

        console.log(
          `✅ Found ${completed.length} completed homework assignments`,
        );
        const completedTokens = completed.map((h) => h.homework_token);

        if (completedTokens.length > 0) {
          // Get homework details
          const { data: homeworkDetails, error: detailsError } =
            await homeworkOps.getHomeworksByTokens(completedTokens);
          if (detailsError) throw new Error('Error fetching homework details');

          if (homeworkDetails && homeworkDetails.length > 0) {
            // Filter by semester with flexible year range

            // More flexible semester filtering - include homework from current and recent years
            const semesterHomeworks = homeworkDetails.filter((hw: any) => {
              if (!hw.created_at) {
                console.log(
                  `- Skipping homework without created_at: ${hw.title}`,
                );
                return false;
              }
              const hwYear = new Date(hw.created_at).getFullYear().toString();
              const currentYear = new Date().getFullYear().toString();
              const semesterYear = parseInt(currentSemester);
              const homeworkYear = parseInt(hwYear);

              // Include homework from current semester year and up to 1 year in either direction
              const isWithinRange = Math.abs(homeworkYear - semesterYear) <= 1;

              if (!isWithinRange) {
                console.log(
                  `- Year too far from semester: ${hw.title} is ${hwYear}, semester ${currentSemester}`,
                );
                return false;
              }

              console.log(`- Including homework: ${hw.title} (${hwYear})`);
              return true;
            });

            console.log(
              '🔍 Semester filtered homework count:',
              semesterHomeworks.length,
            );
            // Enhance with completion data and real performance data
            const enhancedHomeworks = semesterHomeworks.map(
              (homework: any, index) => {
                const studentHomework = completed.find(
                  (sh) => sh.homework_token === homework.homework_token,
                );

                // Calculate real score from question_attempted data
                let realScore = 0;
                if (studentHomework?.question_attempted) {
                  try {
                    const attempted = JSON.parse(
                      studentHomework.question_attempted,
                    );
                    if (
                      typeof attempted === 'object' &&
                      !Array.isArray(attempted)
                    ) {
                      const correctAnswers = Object.values(attempted).filter(
                        (answer) => answer === true,
                      ).length;
                      const totalQuestions = Object.keys(attempted).length;
                      if (totalQuestions > 0) {
                        realScore = Math.round(
                          (correctAnswers / totalQuestions) * 100,
                        );
                      }
                    }
                  } catch (e) {
                    console.warn('Failed to parse question_attempted data:', e);
                  }
                }

                // Calculate real time spent (convert milliseconds to minutes)
                const realTimeSpent = studentHomework?.total_time
                  ? Math.round(studentHomework.total_time / 60000)
                  : 0;

                return {
                  ...homework,
                  completedAt:
                    studentHomework?.completed_at ||
                    studentHomework?.updated_at,
                  timeSpent: realTimeSpent,
                  score: realScore,
                  status:
                    realScore >= 90
                      ? ('excellent' as const)
                      : realScore >= 75
                      ? ('good' as const)
                      : ('needs_improvement' as const),
                  attempts: 1, // For now, we don't track multiple attempts
                } as CompletedHomeworkWithDetails;
              },
            );

            setCompletedHomeworks(enhancedHomeworks);

            // Calculate stats
            const totalScore = enhancedHomeworks.reduce(
              (sum, hw) => sum + (hw.score || 0),
              0,
            );
            const totalTime = enhancedHomeworks.reduce(
              (sum, hw) => sum + (hw.timeSpent || 0),
              0,
            );
            const totalAssignments = studentHomework.length;

            setCompletionStats({
              totalCompleted: enhancedHomeworks.length,
              averageScore:
                enhancedHomeworks.length > 0
                  ? Math.round(totalScore / enhancedHomeworks.length)
                  : 0,
              totalTimeSpent: totalTime,
              completionRate:
                totalAssignments > 0
                  ? Math.round(
                      (enhancedHomeworks.length / totalAssignments) * 100,
                    )
                  : 0,
            });
          }
        }
      }
    } catch (error) {
      console.error('Error fetching completed homework:', error);
      toast({
        title: 'Error loading homework',
        description:
          'Failed to load your completed assignments. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [user.user_token, homeworkOps, toast, currentSemester, error]);

  useEffect(() => {
    if (user.user_token && currentSemester && !error) {
      fetchCompletedHomework();
    }
  }, [fetchCompletedHomework, user.user_token, currentSemester, error]);

  // Filter and sort completed homeworks
  const filteredHomeworks = useMemo(() => {
    let filtered = completedHomeworks;

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(
        (hw) =>
          hw.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          hw.grade.toLowerCase().includes(searchQuery.toLowerCase()),
      );
    }

    // Status filter
    if (filterBy !== 'all') {
      filtered = filtered.filter((hw) => hw.status === filterBy);
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'title':
          return a.title.localeCompare(b.title);
        case 'score':
          return (b.score || 0) - (a.score || 0);
        case 'time_spent':
          return (a.timeSpent || 0) - (b.timeSpent || 0);
        case 'completed_date':
        default:
          return (
            new Date(b.completedAt || b.created_at).getTime() -
            new Date(a.completedAt || a.created_at).getTime()
          );
      }
    });

    return filtered;
  }, [completedHomeworks, searchQuery, filterBy, sortBy]);

  // Handle homework review
  const handleReviewHomework = (homework: CompletedHomeworkWithDetails) => {
    navigate(`/student/completed/${homework.article_token}`);
  };

  // Handle detail view
  const handleViewDetails = (homework: CompletedHomeworkWithDetails) => {
    setSelectedHomework(homework);
    onDetailModalOpen();
  };

  // Table columns for table view
  const tableColumns: TableColumn<CompletedHomeworkWithDetails>[] = [
    {
      key: 'title',
      header: t.tableTitle,
      render: (homework) => (
        <VStack align="start" spacing={1}>
          <Text fontWeight="medium" noOfLines={1}>
            {homework.title}
          </Text>
          <Badge size="sm" colorScheme="blue">
            {t.gradeLabel(homework.grade)}
          </Badge>
        </VStack>
      ),
    },
    {
      key: 'score',
      header: t.tableScore,
      render: (homework) => (
        <VStack align="center" spacing={1}>
          <CircularProgress
            value={homework.score || 0}
            size="40px"
            color={
              (homework.score || 0) >= 90
                ? 'green.400'
                : (homework.score || 0) >= 75
                ? 'yellow.400'
                : 'red.400'
            }
          >
            <CircularProgressLabel fontSize="xs">
              {homework.score}%
            </CircularProgressLabel>
          </CircularProgress>
        </VStack>
      ),
    },
    {
      key: 'timeSpent',
      header: t.tableTimeSpent,
      render: (homework) => (
        <HStack>
          <FiClock />
          <Text fontSize="sm">{t.minutesLong(homework.timeSpent)}</Text>
        </HStack>
      ),
    },
    {
      key: 'completedAt',
      header: t.tableCompleted,
      render: (homework) => (
        <Text fontSize="sm" color="gray.600">
          {formatDate(homework.completedAt || homework.created_at)}
        </Text>
      ),
    },
    {
      key: 'actions',
      header: t.tableActions,
      render: (homework) => (
        <HStack>
          <Tooltip label={t.viewDetails}>
            <IconButton
              aria-label={t.viewDetails}
              icon={<FiInfo />}
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                handleViewDetails(homework);
              }}
            />
          </Tooltip>
          <Tooltip label={t.review}>
            <IconButton
              aria-label={t.review}
              icon={<FiEye />}
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                handleReviewHomework(homework);
              }}
            />
          </Tooltip>
        </HStack>
      ),
    },
  ];

  if (error) {
    return (
      <AdminLayout>
        <VStack spacing={6} align="stretch">
          <Card>
            <VStack spacing={4} py={8}>
              <Text fontSize="lg" fontWeight="600" color="red.500">
                Unable to Load Homework
              </Text>
              <Text color="gray.600" textAlign="center">
                {error}
              </Text>
            </VStack>
          </Card>
        </VStack>
      </AdminLayout>
    );
  }

  if (loading) {
    return (
      <AdminLayout>
        <VStack spacing={6} align="stretch">
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
            {Array(4)
              .fill(0)
              .map((_, i) => (
                <Card key={i}>
                  <Skeleton height="20px" mb={4} />
                  <Skeleton height="40px" />
                </Card>
              ))}
          </SimpleGrid>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
            {Array(6)
              .fill(0)
              .map((_, i) => (
                <Card key={i}>
                  <Skeleton height="150px" mb={4} />
                  <SkeletonText noOfLines={3} />
                </Card>
              ))}
          </SimpleGrid>
        </VStack>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <HStack justify="space-between" align="center">
          <Box>
            <Text fontSize="2xl" fontWeight="bold" color="gray.800">
              {t.headerTitle}
            </Text>
            <HStack spacing={4}>
              <Text color="gray.600">
                {t.completedSubtitle(completionStats.totalCompleted)}
              </Text>
              {currentSemester && (
                <>
                  <Text color="gray.400">•</Text>
                  <HStack>
                    <FiCalendar size={14} />
                    <Text color="gray.600" fontSize="sm">
                      {t.semesterLabel(currentSemester)}
                    </Text>
                  </HStack>
                </>
              )}
            </HStack>
          </Box>
          <HStack spacing={2}>
            <Tooltip label={t.gridView}>
              <IconButton
                aria-label={t.gridView}
                icon={<FiGrid />}
                variant={viewMode === 'grid' ? 'solid' : 'ghost'}
                onClick={() => setViewMode('grid')}
                size="sm"
              />
            </Tooltip>
            <Tooltip label={t.listView}>
              <IconButton
                aria-label={t.listView}
                icon={<FiList />}
                variant={viewMode === 'list' ? 'solid' : 'ghost'}
                onClick={() => setViewMode('list')}
                size="sm"
              />
            </Tooltip>
            <Tooltip label={t.tableView}>
              <IconButton
                aria-label={t.tableView}
                icon={<FiBarChart />}
                variant={viewMode === 'table' ? 'solid' : 'ghost'}
                onClick={() => setViewMode('table')}
                size="sm"
              />
            </Tooltip>
          </HStack>
        </HStack>

        {/* Stats Cards */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
          <StatsCard
            title={t.statsCompleted}
            value={completionStats.totalCompleted.toString()}
            // subtitle="homework assignments"
            icon={FiBookOpen}
          />
          <StatsCard
            title={t.statsAverageScore}
            value={`${completionStats.averageScore}%`}
            // subtitle="overall performance"
            icon={FiTarget}
          />
          <StatsCard
            title={t.statsTimeSpent}
            value={t.timeSpentValue(
              Math.floor(completionStats.totalTimeSpent / 60),
            )}
            subtitle={t.timeSpentSubtitle(completionStats.totalTimeSpent % 60)}
            icon={FiClock}
          />
        </SimpleGrid>

        {/* Main Content */}
        <Tabs variant="line" colorScheme="blue">
          <TabList>
            <Tab>{t.tabsAllCompleted(filteredHomeworks.length)}</Tab>
            <Tab>{t.tabsRecentPerformance}</Tab>
          </TabList>

          <TabPanels>
            <TabPanel px={0}>
              {filteredHomeworks.length === 0 ? (
                <Card>
                  <VStack spacing={4} py={12}>
                    <FiBookOpen size={48} color="gray" />
                    <Text fontSize="lg" color="gray.500">
                      {t.emptyTitle}
                    </Text>
                  </VStack>
                </Card>
              ) : viewMode === 'table' ? (
                <DataTable
                  data={filteredHomeworks}
                  columns={tableColumns}
                  onRowClick={handleReviewHomework}
                />
              ) : (
                <SimpleGrid
                  columns={{
                    base: 1,
                    md: viewMode === 'grid' ? 2 : 1,
                    lg: viewMode === 'grid' ? 3 : 1,
                  }}
                  spacing={4}
                >
                  {filteredHomeworks.map((homework) => (
                    <Card
                      key={homework.homework_token}
                      _hover={{ transform: 'translateY(-2px)', shadow: 'lg' }}
                    >
                      <VStack spacing={4} align="stretch">
                        {/* Header with Score */}
                        <HStack justify="space-between">
                          <VStack spacing={1} align="start" flex={1}>
                            <Text fontWeight="bold" fontSize="lg" noOfLines={2}>
                              {homework.title}
                            </Text>
                            <HStack spacing={2}>
                              <Badge colorScheme="blue" size="sm">
                                {t.gradeLabel(homework.grade)}
                              </Badge>
                              {/* <Badge
                                colorScheme={
                                  homework.status === 'excellent'
                                    ? 'green'
                                    : homework.status === 'good'
                                    ? 'yellow'
                                    : 'red'
                                }
                                size="sm"
                              >
                                {homework.status
                                  ?.replace('_', ' ')
                                  .toUpperCase()}
                              </Badge> */}
                            </HStack>
                          </VStack>
                          <CircularProgress
                            value={homework.score || 0}
                            size="60px"
                            color={
                              (homework.score || 0) >= 90
                                ? 'green.400'
                                : (homework.score || 0) >= 75
                                ? 'yellow.400'
                                : 'red.400'
                            }
                          >
                            <CircularProgressLabel fontSize="sm">
                              {homework.score}%
                            </CircularProgressLabel>
                          </CircularProgress>
                        </HStack>

                        {/* Stats */}
                        <SimpleGrid columns={3} spacing={4} fontSize="sm">
                          <VStack spacing={1}>
                            <HStack>
                              <FiClock />
                              <Text color="gray.600">{t.timeLabel}</Text>
                            </HStack>
                            <Text fontWeight="bold">
                              {t.minutesShort(homework.timeSpent)}
                            </Text>
                          </VStack>
                          {/* <VStack spacing={1}>
                            <HStack>
                              <FiTarget />
                              <Text color="gray.600">{t.attemptsLabel}</Text>
                            </HStack>
                            <Text fontWeight="bold">{homework.attempts}</Text>
                          </VStack> */}
                          <VStack spacing={1}>
                            <HStack>
                              <FiCalendar />
                              <Text color="gray.600">{t.completedLabel}</Text>
                            </HStack>
                            <Text fontWeight="bold" fontSize="xs">
                              {
                                formatDate(
                                  homework.completedAt || homework.created_at,
                                ).split(' ')[0]
                              }
                            </Text>
                          </VStack>
                        </SimpleGrid>

                        {/* Action Buttons */}
                        <HStack spacing={2}>
                          <Button
                            leftIcon={<FiEye />}
                            variant="secondary"
                            size="sm"
                            flex={1}
                            onClick={() => handleReviewHomework(homework)}
                          >
                            {t.review}
                          </Button>
                          <Tooltip label={t.viewDetails}>
                            <IconButton
                              aria-label={t.viewDetails}
                              icon={<FiInfo />}
                              size="sm"
                              variant="ghost"
                              onClick={() => handleViewDetails(homework)}
                            />
                          </Tooltip>
                        </HStack>
                      </VStack>
                    </Card>
                  ))}
                </SimpleGrid>
              )}
            </TabPanel>

            <TabPanel px={0}>
              <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
                <Card>
                  <VStack spacing={4} align="stretch">
                    <Text fontWeight="bold" fontSize="lg">
                      {t.recentScoresTrend}
                    </Text>
                    <VStack spacing={3}>
                      {filteredHomeworks.slice(0, 5).map((homework, index) => (
                        <HStack
                          key={homework.homework_token}
                          justify="space-between"
                          w="full"
                        >
                          <Text fontSize="sm" noOfLines={1} flex={1}>
                            {homework.title}
                          </Text>
                          <Progress
                            value={homework.score || 0}
                            size="lg"
                            colorScheme={
                              (homework.score || 0) >= 90
                                ? 'green'
                                : (homework.score || 0) >= 75
                                ? 'yellow'
                                : 'red'
                            }
                            w="100px"
                          />
                          <Text fontSize="sm" minW="40px">
                            {homework.score}%
                          </Text>
                        </HStack>
                      ))}
                    </VStack>
                  </VStack>
                </Card>

                <Card>
                  <VStack spacing={4} align="stretch">
                    <Text fontWeight="bold" fontSize="lg">
                      {t.performanceSummary}
                    </Text>
                    <Stat textAlign="center">
                      <StatLabel>{t.bestScore}</StatLabel>
                      <StatNumber color="green.500">
                        {Math.max(
                          ...filteredHomeworks.map((h) => h.score || 0),
                        )}
                        %
                      </StatNumber>
                      <StatHelpText>
                        <StatArrow type="increase" />
                        {t.personalBest}
                      </StatHelpText>
                    </Stat>
                  </VStack>
                </Card>
              </SimpleGrid>
            </TabPanel>
          </TabPanels>
        </Tabs>

        {/* Detail Modal */}
        <Modal
          isOpen={isDetailModalOpen}
          onClose={onDetailModalClose}
          size="4xl"
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              {t.homeworkDetails}
              {selectedHomework?.title ? `: ${selectedHomework.title}` : ''}
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              {selectedHomework && (
                <VStack spacing={6} align="stretch">
                  {/* Summary Stats */}
                  <SimpleGrid columns={4} spacing={4}>
                    <Box
                      textAlign="center"
                      p={4}
                      borderWidth={1}
                      borderRadius="md"
                    >
                      <Text fontSize="2xl" fontWeight="bold" color="blue.500">
                        {selectedHomework.score}%
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        {t.finalScore}
                      </Text>
                    </Box>
                    <Box
                      textAlign="center"
                      p={4}
                      borderWidth={1}
                      borderRadius="md"
                    >
                      <Text fontSize="2xl" fontWeight="bold" color="green.500">
                        {t.minutesShort(selectedHomework.timeSpent)}
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        {t.timeSpentLabel}
                      </Text>
                    </Box>
                    {/* <Box
                      textAlign="center"
                      p={4}
                      borderWidth={1}
                      borderRadius="md"
                    >
                      <Text fontSize="2xl" fontWeight="bold" color="yellow.500">
                        {selectedHomework.attempts}
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        {t.attemptsLabelDetail}
                      </Text>
                    </Box> */}
                    <Box
                      textAlign="center"
                      p={4}
                      borderWidth={1}
                      borderRadius="md"
                    >
                      <Text fontSize="lg" fontWeight="bold" color="purple.500">
                        {t.gradeLabel(selectedHomework.grade)}
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        {t.levelLabel}
                      </Text>
                    </Box>
                  </SimpleGrid>

                  {/* Performance Analysis */}
                  <Box>
                    <Text fontWeight="bold" mb={3}>
                      {t.performanceAnalysis}
                    </Text>
                    <VStack spacing={2} align="start">
                      <Text fontSize="sm">
                        <strong>{t.statusLabel}:</strong>
                        <Badge
                          ml={2}
                          colorScheme={
                            selectedHomework.status === 'excellent'
                              ? 'green'
                              : selectedHomework.status === 'good'
                              ? 'yellow'
                              : 'red'
                          }
                        >
                          {selectedHomework.status
                            ?.replace('_', ' ')
                            .toUpperCase()}
                        </Badge>
                      </Text>
                      <Text fontSize="sm">
                        <strong>{t.completedAtLabel}:</strong>{' '}
                        {formatDate(
                          selectedHomework.completedAt ||
                            selectedHomework.created_at,
                        )}
                      </Text>
                      <Text fontSize="sm">
                        <strong>{t.timeEfficiencyLabel}:</strong>{' '}
                        {(selectedHomework.timeSpent || 0) < 15
                          ? t.timeEfficiencyFast
                          : (selectedHomework.timeSpent || 0) < 30
                          ? t.timeEfficiencyAverage
                          : t.timeEfficiencyThorough}
                      </Text>
                    </VStack>
                  </Box>

                  {/* Action Buttons */}
                  <HStack justify="flex-end" spacing={3}>
                    <Button variant="secondary" onClick={onDetailModalClose}>
                      {t.close}
                    </Button>
                    <Button
                      leftIcon={<FiEye />}
                      colorScheme="blue"
                      onClick={() => {
                        handleReviewHomework(selectedHomework);
                        onDetailModalClose();
                      }}
                    >
                      {t.reviewHomework}
                    </Button>
                  </HStack>
                </VStack>
              )}
            </ModalBody>
          </ModalContent>
        </Modal>
      </VStack>
    </AdminLayout>
  );
};
